// src/core/parser.ts

import type { SectionData } from "../types";

// Split on commas that are OUTSIDE quotes/braces/brackets/parentheses.
function splitOnCommasOutsideGroups(input: string): string[] {
  const parts: string[] = [];
  let current = "";
  let depth = 0; // {}, [], ()
  let inSingle = false;
  let inDouble = false;

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];

    // toggle quotes (but not when escaped)
    if (ch === "'" && !inDouble) {
      const prev = input[i - 1];
      if (prev !== "\\") inSingle = !inSingle;
      current += ch;
      continue;
    }
    if (ch === '"' && !inSingle) {
      const prev = input[i - 1];
      if (prev !== "\\") inDouble = !inDouble;
      current += ch;
      continue;
    }

    if (!inSingle && !inDouble) {
      if (ch === "{" || ch === "[" || ch === "(") {
        depth++;
        current += ch;
        continue;
      }
      if (ch === "}" || ch === "]" || ch === ")") {
        if (depth > 0) depth--;
        current += ch;
        continue;
      }
      if (ch === "," && depth === 0) {
        const tok = current.trim();
        if (tok !== "") parts.push(tok);
        current = "";
        continue;
      }
    }

    current += ch;
  }

  const tail = current.trim();
  if (tail !== "") parts.push(tail);
  return parts;
}

/**
 * Parses raw markdown into structured sections with metadata.
 */
export function parseMarkdown(md: string): SectionData[] {
  return md
    .split("---section")
    .filter(Boolean)
    .map((block) => {
      const lines = block.trim().split("\n");
      const attrs: Record<string, any> = {};
      const content: string[] = [];

      let inMeta = true;
      let currentKey: string | null = null;

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();

        if (inMeta && trimmed === "") {
          inMeta = false;
          continue;
        }

        if (inMeta) {
          if (/^\s+\S/.test(line) && currentKey) {
            // Indented sub-property
            const [subKey, ...subRest] = trimmed.split(":");
            const subVal = subRest.join(":").trim();
            if (typeof attrs[currentKey] !== "object") attrs[currentKey] = {};
            attrs[currentKey][subKey.trim()] = coerceValue(subVal);
          } else {
            const [rawKey, ...rest] = line.split(":");
            const key = rawKey.trim();
            const val = rest.join(":").trim();
            const nextLine = lines[i + 1]?.trim();

            // Start of nested block (e.g., key: \n  sub: val) — keep existing behavior
            if (val === "" || (/^\s+\S/.test(nextLine))) {
              attrs[key] = {};
              currentKey = key;
              continue;
            }

            // Brace/quote-aware comma handling.
            // - DO NOT split for numbered media keys (imageN/overlayN)
            // - DO NOT split when the value contains a { ... } body (we want the whole spec intact)
            // Example that SHOULD split:   video-v: clip.mp4, loop: false, muted: true
            // Example that should NOT split: image1: ror.png { width: 250, position: top-left, ... }
            const isNumberedMediaKey = /^(image|overlay)\d+$/i.test(key);
            const parts = splitOnCommasOutsideGroups(val);
            // quick guard: any brace body present => treat as a single spec
            const hasBraceBody = val.includes("{") && val.includes("}");

            if (!isNumberedMediaKey && !hasBraceBody && parts.length > 1) {
              const [primaryVal, ...options] = parts;

              // First token is the main value for `key`
              attrs[key] = coerceValue(primaryVal);

              // Remaining tokens should be key:value pairs — add them as top-level attrs
              for (const opt of options) {
                const idx = opt.indexOf(":");
                if (idx === -1) continue;
                const optKey = opt.slice(0, idx).trim();
                const optVal = opt.slice(idx + 1).trim();
                if (optKey) attrs[optKey] = coerceValue(optVal);
              }

              currentKey = null;
            } else {
              // Simple scalar (or complex inline spec kept intact, e.g., image1/overlay1 with { ... })
              attrs[key] = coerceValue(val);
              currentKey = null;
            }
          }
        } else {
          content.push(line);
        }
      }

      return {
        attrs,
        content: content.join("\n"),
      };
    });
}

/**
 * Converts string values to appropriate JS types.
 */
export function coerceValue(val: string): any {
  const trimmed = val.trim();
  const lower = trimmed.toLowerCase();

  if (lower === "true") return true;
  if (lower === "false") return false;
  if (lower === "null") return null;
  if (trimmed !== "" && !isNaN(Number(trimmed))) return parseFloat(trimmed);

  // Return the trimmed original value for strings
  return trimmed;
}